import { MapInstance } from '@/types/map';
import { useMapInstance } from '@/utils/context/mapInstanceContext';
import Map from 'ol/Map';
import { Zoom } from 'ol/control';
import React, { MutableRefObject, useEffect } from 'react';
import { useOlMapLayers } from '@/components/Map/MapViewer/utils/config/useOlMapLayers';
import { defaults, MouseWheelZoom } from 'ol/interaction';
import { ONE, ZERO } from '@/constants/common';
import { FeatureLike } from 'ol/Feature';
import { Pixel } from 'ol/pixel';
import { useOlMapView } from './config/useOlMapView';
import { useOlMapListeners } from './listeners/useOlMapListeners';

interface UseOlMapInput {
  target?: HTMLElement;
  info?: HTMLElement;
}
interface UseOlMapOutput {
  mapRef: MutableRefObject<null | HTMLDivElement>;
  infoRef: MutableRefObject<null | HTMLDivElement>;
  mapInstance: MapInstance;
}

type UseOlMap = (input?: UseOlMapInput) => UseOlMapOutput;

export const useOlMap: UseOlMap = ({ target, info } = {}) => {
  const mapRef = React.useRef<null | HTMLDivElement>(null);
  const infoRef = React.useRef<null | HTMLDivElement>(null);
  const { mapInstance, handleSetMapInstance } = useMapInstance();
  const view = useOlMapView({ mapInstance });

  const mapLayers = useOlMapLayers({ mapInstance });

  useOlMapListeners({ view, mapInstance });

  useEffect(() => {
    // checking if innerHTML is empty due to possibility of target element cloning by OpenLayers map instance
    if (!mapRef.current || mapRef.current.innerHTML !== '') {
      return;
    }

    if (!infoRef.current) {
      return;
    }

    const map = new Map({
      interactions: defaults({
        mouseWheelZoom: false,
      }).extend([
        new MouseWheelZoom({
          duration: 250,
          timeout: 80,
        }),
      ]),
      target: target || mapRef.current,
    });

    // remove zoom controls as we are using our own
    map.getControls().forEach(mapControl => {
      if (mapControl instanceof Zoom) {
        map.removeControl(mapControl);
      }
    });

    const infoDiv = info || infoRef.current;

    const displayFeatureInfo = (pixel: Pixel): void => {
      let currentFeature: FeatureLike | undefined;
      const features: FeatureLike[] = map.getFeaturesAtPixel(pixel);
      features.forEach(feature => {
        if (feature && feature.get('tooltip')) {
          // eslint-disable-next-line no-magic-numbers
          const top = (mapRef.current?.getBoundingClientRect().top ?? ZERO) - 30;
          const left = mapRef.current?.getBoundingClientRect().left ?? ZERO;
          infoDiv.style.left = `${pixel[ZERO] + left}px`;
          infoDiv.style.top = `${pixel[ONE] + top}px`;
          infoDiv.style.visibility = 'visible';
          infoDiv.style.textAlign = 'left';
          infoDiv.innerText = feature.get('tooltip');
          currentFeature = feature;
        }
      });
      if (!currentFeature) {
        infoDiv.style.visibility = 'hidden';
      }
    };

    map.on('pointermove', evt => {
      if (evt.dragging) {
        infoDiv.style.visibility = 'hidden';
        return;
      }
      displayFeatureInfo(evt.pixel);
    });

    handleSetMapInstance(map);
  }, [target, info, handleSetMapInstance]);

  useEffect(() => {
    if (!mapInstance) {
      return;
    }
    mapInstance.setLayers(mapLayers);
  }, [mapInstance, mapLayers]);

  return {
    mapRef,
    infoRef,
    mapInstance,
  };
};
